import { useId } from 'react';
import { Box, InputAdornment, Slider, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { clamp, normalizeHex, readableTextColor } from '../lib/color';

interface SliderFieldProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  unit?: string;
  disabled?: boolean;
  onChange: (value: number) => void;
}

/** Slider plus a number input, sharing one accessible label. */
export function SliderField({
  label,
  value,
  min,
  max,
  step = 1,
  unit,
  disabled = false,
  onChange,
}: SliderFieldProps) {
  const id = useId();
  const decimals = step < 1 ? 1 : 0;

  return (
    <Box sx={{ opacity: disabled ? 0.5 : 1 }}>
      <Typography id={id} variant="body2" sx={{ mb: 0.25 }}>
        {label}
      </Typography>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
        <Slider
          value={value}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          aria-labelledby={id}
          valueLabelDisplay="auto"
          onChange={(_, next) => onChange(clamp(next as number, min, max))}
          sx={{ flex: 1 }}
        />
        <TextField
          type="number"
          value={Number(value.toFixed(decimals))}
          disabled={disabled}
          aria-label={label}
          onChange={(event) => onChange(clamp(Number(event.target.value), min, max))}
          slotProps={{
            htmlInput: { min, max, step, inputMode: 'decimal' },
            input: unit
              ? { endAdornment: <InputAdornment position="end">{unit}</InputAdornment> }
              : undefined,
          }}
          sx={{ width: unit ? 104 : 84, flexShrink: 0 }}
        />
      </Stack>
    </Box>
  );
}

interface ColorFieldProps {
  label: string;
  value: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}

export function ColorField({ label, value, disabled = false, onChange }: ColorFieldProps) {
  const id = useId();
  const hex = normalizeHex(value);

  return (
    <Box sx={{ opacity: disabled ? 0.5 : 1 }}>
      <Typography component="label" htmlFor={id} variant="body2" sx={{ mb: 0.25, display: 'block' }}>
        {label}
      </Typography>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Tooltip title={hex}>
          <Box
            component="input"
            id={id}
            type="color"
            value={hex}
            disabled={disabled}
            onChange={(event) => onChange(event.target.value)}
            sx={{
              width: 48,
              height: 36,
              borderRadius: 1.5,
              flexShrink: 0,
              boxShadow: `inset 0 0 0 1px ${readableTextColor(hex)}33`,
            }}
          />
        </Tooltip>
        <TextField
          value={hex}
          disabled={disabled}
          aria-label={`${label} (HEX)`}
          onChange={(event) => onChange(normalizeHex(event.target.value, hex))}
          slotProps={{ htmlInput: { maxLength: 7, spellCheck: false } }}
          sx={{ width: 110, '& input': { fontFamily: 'ui-monospace, monospace' } }}
        />
      </Stack>
    </Box>
  );
}
